import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartBar, FileXls, FileCsv } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const iso = (d) => d.toISOString().slice(0, 10);
const inputCls = "h-9 w-40 bg-transparent border-white/10 text-white text-xs [color-scheme:dark]";

export const AdsReportCard = () => {
  const today = new Date();
  const [configured, setConfigured] = useState(false);
  const [start, setStart] = useState(iso(new Date(today.getTime() - 29 * 864e5)));
  const [end, setEnd] = useState(iso(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get("/admin/ads/config").then(({ data }) => setConfigured(!!data.configured)).catch(() => {});
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ads/report?start=${start}&end=${end}`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/admin/ads/report/export?start=${start}&end=${end}&fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-google-ads-${start}-${end}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report ${fmt.toUpperCase()} scaricato`);
    } catch (err) {
      toast.error("Esportazione non riuscita: collega prima Google Ads");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border mt-6 animate-fade-up" data-testid="ads-report-card">
      <div className="flex items-start gap-3 mb-5">
        <ChartBar size={28} weight="duotone" className="text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Report campagne</h2>
          <p className="text-sm text-slate-400 mt-0.5">Spesa, clic, impressioni e conversioni per il periodo scelto, con esportazione CSV o Excel.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/50">Dal</span>
          <Input type="date" data-testid="report-start-input" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
          <span className="text-[11px] text-white/50">al</span>
          <Input type="date" data-testid="report-end-input" value={end} onChange={(e) => setEnd(e.target.value)} min={start} className={inputCls} />
        </div>
        <Button data-testid="report-generate-button" onClick={loadReport} disabled={loading || !configured}
          className="h-9 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold disabled:opacity-40">
          {loading ? "Caricamento..." : "Genera report"}
        </Button>
        <div className="flex gap-2 ml-auto">
          <Button data-testid="report-export-csv" onClick={() => download("csv")} disabled={!!exporting || !configured} variant="ghost"
            className="h-9 px-4 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200 rounded-full text-xs font-semibold disabled:opacity-40">
            <FileCsv size={14} className="mr-1.5" /> {exporting === "csv" ? "..." : "CSV"}
          </Button>
          <Button data-testid="report-export-xlsx" onClick={() => download("xlsx")} disabled={!!exporting || !configured} variant="ghost"
            className="h-9 px-4 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200 rounded-full text-xs font-semibold disabled:opacity-40">
            <FileXls size={14} className="mr-1.5" /> {exporting === "xlsx" ? "..." : "Excel"}
          </Button>
        </div>
      </div>
      {!configured && (
        <p className="text-[11px] text-amber-400/80 mt-3" data-testid="report-not-configured">
          Il report sarà disponibile quando l'account Google Ads sarà collegato.
        </p>
      )}
      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="report-totals">
            {[["Spesa", `€ ${report.totals.spend.toFixed(2)}`], ["Clic", report.totals.clicks],
              ["Impressioni", report.totals.impressions], ["Conversioni", report.totals.conversions]].map(([l, v]) => (
              <div key={l} className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">{l}</p>
                <p className="text-xl font-bold mt-0.5">{v}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto mt-5">
            <table className="w-full text-xs" data-testid="report-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
                  <th className="py-2 pr-2 font-medium">Campagna</th>
                  <th className="py-2 pr-2 font-medium">Stato</th>
                  <th className="py-2 pr-2 font-medium text-right">Spesa</th>
                  <th className="py-2 pr-2 font-medium text-right">Clic</th>
                  <th className="py-2 pr-2 font-medium text-right">Impressioni</th>
                  <th className="py-2 pr-2 font-medium text-right">Conv.</th>
                  <th className="py-2 pr-2 font-medium text-right">CTR</th>
                  <th className="py-2 font-medium text-right">CPC</th>
                </tr>
              </thead>
              <tbody>
                {report.campaigns.length === 0 && (
                  <tr><td colSpan={8} className="py-4 text-center text-white/40">Nessun dato nel periodo selezionato.</td></tr>
                )}
                {report.campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-white/5 text-white/75">
                    <td className="py-2 pr-2 font-semibold truncate max-w-[200px]" title={c.name}>{c.name}</td>
                    <td className="py-2 pr-2 text-white/45">{c.status}</td>
                    <td className="py-2 pr-2 text-right font-semibold">€ {c.spend.toFixed(2)}</td>
                    <td className="py-2 pr-2 text-right">{c.clicks}</td>
                    <td className="py-2 pr-2 text-right">{c.impressions}</td>
                    <td className="py-2 pr-2 text-right">{c.conversions}</td>
                    <td className="py-2 pr-2 text-right">{c.ctr}%</td>
                    <td className="py-2 text-right">€ {c.cpc.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
